import { V2, hoverOnly } from '../../theme/tokens'
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Slider,
  Stack,
  Chip,
  Alert,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as ApproveIcon,
  Block as ExcludeIcon,
  SwapHoriz as SwapIcon,
  AutoMode as AutoIcon,
} from '@mui/icons-material';
import { OVERRIDE_DECISIONS, DECISION_LABELS } from './hooks/useOverrides';
import { RELATION_COLORS, RELATION_LABELS } from './FilterControls';
import RecommendationScoreBar from './RecommendationScoreBar';
import useFokusNachLaden from '../common/hooks/useFokusNachLaden';
import { formatNumber } from '../../utils/format';

/**
 * Dialog for creating/editing recommendation overrides.
 */
export default function OverrideDialog({
  open,
  onClose,
  recommendation,
  sourceProduct,
  onSave,
  isLoading = false,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Form state
  const [decision, setDecision] = useState(OVERRIDE_DECISIONS.APPROVE);
  const [weightAdjustment, setWeightAdjustment] = useState(0);
  const [note, setNote] = useState('');
  const [error, setError] = useState(null);
  // 🔴 K-F1 (2026-10-10): der Knopf sperrt sich beim Schreiben und verliert
  // dabei den Fokus — der Haken holt ihn danach zurueck.
  const speichernKnopf = useRef(null);
  useFokusNachLaden(speichernKnopf, isLoading);

  // Initialize from existing override
  useEffect(() => {
    if (recommendation?.override) {
      setDecision(recommendation.override.decision);
      setWeightAdjustment(recommendation.override.weight_adjustment || 0);
      setNote(recommendation.override.note || '');
    } else {
      setDecision(OVERRIDE_DECISIONS.APPROVE);
      setWeightAdjustment(0);
      setNote('');
    }
    setError(null);
  }, [recommendation, open]);

  const handleSave = async () => {
    if (!recommendation) return;

    try {
      setError(null);
      await onSave({
        source_product_id: sourceProduct?.product_id || recommendation.source_product_id,
        target_product_id: recommendation.target_product_id,
        relation_type: recommendation.relation_type,
        decision,
        weight_adjustment: weightAdjustment,
        note: note.trim() || null,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to save override');
    }
  };

  const handleClearOverride = async () => {
    if (!recommendation) return;

    try {
      setError(null);
      await onSave({
        source_product_id: sourceProduct?.product_id || recommendation.source_product_id,
        target_product_id: recommendation.target_product_id,
        relation_type: recommendation.relation_type,
        decision: OVERRIDE_DECISIONS.AUTO,
        weight_adjustment: 0,
        note: null,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to clear override');
    }
  };

  if (!recommendation) return null;

  const hasExistingOverride = !!recommendation.override;
  const relationColor = RELATION_COLORS[recommendation.relation_type] || V2.ink45;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { overflow: 'visible' },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" component="span">
            {hasExistingOverride ? 'Edit Override' : 'Create Override'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Product Info */}
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            {recommendation.image_url && (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.paper',
                }}
              >
                <img
                  src={recommendation.image_url}
                  alt={recommendation.product_name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {recommendation.product_name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {recommendation.vendor}
              </Typography>
            </Box>
            <Stack alignItems="flex-end" spacing={0.5}>
              <Chip
                label={RELATION_LABELS[recommendation.relation_type] || recommendation.relation_type}
                size="small"
                sx={{
                  bgcolor: `${relationColor}20`,
                  color: relationColor,
                  fontWeight: 500,
                }}
              />
              <RecommendationScoreBar
                score={recommendation.score}
                size="small"
                sx={{ width: 80 }}
              />
            </Stack>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Decision Type */}
        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
          <FormLabel component="legend" sx={{ mb: 1, fontWeight: 500 }}>
            Override Decision
          </FormLabel>
          <RadioGroup
            value={decision}
            onChange={(e) => setDecision(e.target.value)}
          >
            <Stack spacing={1}>
              <DecisionOption
                value={OVERRIDE_DECISIONS.APPROVE}
                label={DECISION_LABELS[OVERRIDE_DECISIONS.APPROVE]}
                description="Keep this recommendation as-is"
                icon={<ApproveIcon color="success" />}
                selected={decision === OVERRIDE_DECISIONS.APPROVE}
              />
              <DecisionOption
                value={OVERRIDE_DECISIONS.FORCE_COMPLEMENT}
                label={DECISION_LABELS[OVERRIDE_DECISIONS.FORCE_COMPLEMENT]}
                description="Override to show as a complementary product"
                icon={<SwapIcon sx={{ color: RELATION_COLORS.complement }} />}
                selected={decision === OVERRIDE_DECISIONS.FORCE_COMPLEMENT}
              />
              <DecisionOption
                value={OVERRIDE_DECISIONS.FORCE_ADDON}
                label={DECISION_LABELS[OVERRIDE_DECISIONS.FORCE_ADDON]}
                description="Override to show as an add-on product"
                icon={<SwapIcon sx={{ color: RELATION_COLORS.addon }} />}
                selected={decision === OVERRIDE_DECISIONS.FORCE_ADDON}
              />
              <DecisionOption
                value={OVERRIDE_DECISIONS.FORCE_COMPETITOR}
                label={DECISION_LABELS[OVERRIDE_DECISIONS.FORCE_COMPETITOR]}
                description="Override to show as a competitor product"
                icon={<SwapIcon sx={{ color: RELATION_COLORS.competitor }} />}
                selected={decision === OVERRIDE_DECISIONS.FORCE_COMPETITOR}
              />
              <DecisionOption
                value={OVERRIDE_DECISIONS.EXCLUDE}
                label={DECISION_LABELS[OVERRIDE_DECISIONS.EXCLUDE]}
                description="Never show this recommendation"
                icon={<ExcludeIcon color="error" />}
                selected={decision === OVERRIDE_DECISIONS.EXCLUDE}
              />
            </Stack>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        {/* Weight Adjustment */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Score Adjustment
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Adjust the recommendation score up or down
          </Typography>
          <Box sx={{ px: 1 }}>
            <Slider
              value={weightAdjustment}
              onChange={(_, value) => setWeightAdjustment(value)}
              min={-0.2}
              max={0.2}
              step={0.01}
              marks={[
                { value: -0.2, label: '-0.2' },
                { value: -0.1, label: '-0.1' },
                { value: 0, label: '0' },
                { value: 0.1, label: '+0.1' },
                { value: 0.2, label: '+0.2' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => v > 0 ? `+${formatNumber(v)}` : formatNumber(v)}
            />
          </Box>
        </Box>

        {/* Note */}
        <TextField
          label="Note (optional)"
          placeholder="Add a note explaining this override…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={2}
          fullWidth
          sx={{ mb: 1 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {hasExistingOverride && (
          <Button
            onClick={handleClearOverride}
            color="inherit"
            disabled={isLoading}
            startIcon={<AutoIcon />}
          >
            Clear override
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        {/* 🔴 K-F1 (2026-10-10): der Lauf zeigt sich am Knopf selbst — Ring
            statt Wortwechsel. `loadingPosition="start"` haelt die
            Beschriftung lesbar (die Vorgabe `center` setzt `color:
            transparent`). Die RUHE-Beschriftung bleibt stehen. */}
        <Button
          ref={speichernKnopf}
          onClick={handleSave}
          variant="contained"
          disabled={isLoading}
          loading={isLoading}
          loadingPosition="start"
          aria-busy={isLoading || undefined}
        >
          {hasExistingOverride ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Decision option card for the radio group.
 */
function DecisionOption({ value, label, description, icon, selected }) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio size="small" />}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          {icon}
          <Box>
            <Typography variant="body2" fontWeight={selected ? 600 : 400}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          </Box>
        </Box>
      }
      sx={{
        m: 0,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'action.selected' : 'transparent',
        ...hoverOnly({
          bgcolor: 'action.hover',
        }),
      }}
    />
  );
}
